import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { ArrowRight, Play, Sun, Leaf, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

// Texts that will animate
const changingTexts = ["with Solar Energy", "with Zero Emissions", "with Smart Savings"];

// 🌟 Smooth Live Counter Component
const Counter = ({ value, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const end = parseInt(value, 10);
    if (start === end) return;

    let totalDuration = 2000;
    let incrementTime = Math.abs(Math.floor(totalDuration / end));
    if (incrementTime < 16) incrementTime = 16; 
    
    const step = Math.ceil(end / (totalDuration / incrementTime));

    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, incrementTime);

    return () => clearInterval(timer);
  }, [value, isInView]);

  return (
    <span ref={ref} className="tabular-nums">
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

const HeroSection = () => {
  const [textIndex, setTextIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTextIndex((prev) => (prev + 1) % changingTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const scrollToContact = (e) => {
    e.preventDefault();
    const contactSection = document.getElementById("contact");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section className="relative w-full min-h-[100svh] flex flex-col justify-center items-center overflow-hidden bg-black pt-20 sm:pt-24 lg:pt-32 pb-12 sm:pb-16">
      
      {/* 🌟 1. BACKGROUND VIDEO & CLEAN OVERLAY 🌟 */}
      <div className="absolute inset-0 w-full h-full z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          poster="https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1920&q=90"
        >
          <source
            src="https://videos.pexels.com/video-files/854938/854938-hd_1920_1080_30fps.mp4"
            type="video/mp4"
          />
        </video>
        
        {/* CLEAN GRADIENT: Dark at top/bottom for readability, slightly transparent in middle */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/50 to-black/95" />
      </div>

      {/* 🌟 2. MAIN HERO CONTENT 🌟 */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center justify-center my-auto">
        
        {/* Sleek Tech Badge */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-3 py-1 sm:px-5 sm:py-2 rounded-full mb-5 sm:mb-8"
        >
          <span className="relative flex h-2 w-2 sm:h-2.5 sm:w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4CAF3E] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 sm:h-2.5 sm:w-2.5 bg-[#4CAF3E]"></span>
          </span>
          <span className="text-[9px] sm:text-xs font-bold tracking-[0.15em] sm:tracking-[0.2em] text-white uppercase">
            Future of Clean Energy
          </span>
        </motion.div>

        {/* Massive Premium Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
          className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[75px] font-black text-white tracking-tight leading-[1.15] sm:leading-[1.1] max-w-5xl mx-auto drop-shadow-xl"
        >
          Power Your Future <br />
          {/* Dynamic Height container to prevent layout shifts across viewports */}
          <div className="relative inline-block h-[38px] xs:h-[45px] sm:h-[65px] md:h-[75px] lg:h-[90px] xl:h-[105px] w-full mt-1.5 sm:mt-2">
            <AnimatePresence mode="wait">
              <motion.span
                key={textIndex}
                initial={{ y: "80%", opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: "-80%", opacity: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="absolute left-0 right-0 mx-auto bg-gradient-to-r from-[#FFC107] via-[#F7941D] to-[#FFC107] bg-clip-text text-transparent font-black tracking-tight"
                style={{ filter: 'drop-shadow(0px 4px 12px rgba(247, 148, 29, 0.35))' }}
              >
                {changingTexts[textIndex]}
              </motion.span>
            </AnimatePresence>
          </div>
        </motion.h1>

        {/* Clean Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-[13px] sm:text-base lg:text-lg text-slate-200 max-w-2xl mx-auto font-normal leading-relaxed mt-5 sm:mt-6 mb-8 sm:mb-10 px-2 sm:px-0 drop-shadow-md"
        >
          Transform your residential & commercial properties with highly efficient, grid-optimized solar systems. Secure your energy independence today.
        </motion.p>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="flex flex-col sm:flex-row justify-center items-center gap-3.5 sm:gap-4 w-full max-w-[280px] sm:max-w-md mx-auto"
        >
          {/* Primary Button: Glowing Orange */}
          <button
            onClick={scrollToContact}
            className="group w-full sm:w-auto relative flex items-center justify-center gap-2 bg-gradient-to-r from-[#F7941D] to-[#E67A00] text-white font-bold text-[12px] sm:text-[14px] py-3.5 sm:py-4 px-6 sm:px-8 rounded-full tracking-wider uppercase transition-all duration-300 hover:shadow-[0_0_25px_rgba(247,148,29,0.5)] hover:scale-105 active:scale-95"
          >
            <span>Get Free Quote</span>
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>

          {/* Secondary Button: Premium Glass Outline */}
          <Link
            to="/projectpage"
            className="group w-full sm:w-auto relative flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 backdrop-blur-md border border-white/30 text-white font-bold text-[12px] sm:text-[14px] py-3.5 sm:py-4 px-6 sm:px-8 rounded-full tracking-wider uppercase transition-all duration-300 hover:scale-105 active:scale-95"
          >
            <Play className="w-4 h-4 text-white group-hover:text-[#F7941D] transition-colors" />
            <span>Our Projects</span>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;